import { Badge, Skeleton } from '@common/components';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';

import { PREVIEW_SIZE } from '../../constants';

export type PopularPolicySource = 'peer' | 'neighbor';

export interface PopularPolicyData {
  id: string;
  title: string;
  benefit: string;
  source?: PopularPolicySource;
  ddayText: string;
  isUrgent: boolean;
}

interface PopularPoliciesProps {
  policies: PopularPolicyData[];
}

const SOURCE_LABEL: Record<PopularPolicySource, string> = {
  peer: '또래',
  neighbor: '이웃',
};

export function PopularPolicies({ policies }: PopularPoliciesProps) {
  const isEmpty = policies.length < 1;

  return (
    <aside className="flex w-250 shrink-0 flex-col gap-8 mobile-tablet:hidden">
      <h2 className="font-700 text-Gray-900 typo-body3">인기 정책</h2>
      {isEmpty
        ? Array.from({ length: PREVIEW_SIZE }, (_, index) => (
            <PopularPolicySkeleton key={index} />
          ))
        : null}
      {policies.map((policy) => (
        <CustomLink
          key={policy.id}
          href={ROUTES.POLICY_DETAIL.pathname({ idIdx: policy.id })}
          className="flex w-full cursor-pointer flex-col overflow-hidden rounded-medium border border-Gray-100 bg-Gray-white p-20 transition-colors hover:bg-Gray-25"
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-4">
              {policy.source && (
                <Badge
                  className={cn('w-fit text-Gray-white', {
                    'bg-M-400': policy.source === 'peer',
                    'bg-Pink-300': policy.source === 'neighbor',
                  })}
                >
                  {SOURCE_LABEL[policy.source]}
                </Badge>
              )}
              <Badge
                className={cn('w-fit', {
                  'text-Gray-black': !policy.isUrgent,
                  'text-R-400': policy.isUrgent,
                })}
              >
                {policy.ddayText}
              </Badge>
            </div>
            <p className="truncate font-700 tracking-[-0.28px] text-Gray-900 typo-body4">
              {policy.title}
            </p>
            <div className="h-19">
              <p className="truncate font-600 text-Gray-550 typo-caption1">
                {policy.benefit}
              </p>
            </div>
          </div>
        </CustomLink>
      ))}
    </aside>
  );
}

function PopularPolicySkeleton() {
  return (
    <div className="flex w-full flex-col overflow-hidden rounded-medium border border-Gray-100 bg-Gray-white p-20">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          <Skeleton className="h-20 w-32 rounded-xsmall" />
          <Skeleton className="h-20 w-40 rounded-xsmall" />
        </div>
        <Skeleton className="mt-4 h-20 w-full rounded-xxsmall" />
        <Skeleton className="mt-2 h-19 w-3/4 rounded-xxsmall" />
      </div>
    </div>
  );
}
